import { DurationLikeObject, Duration as DurationLuxon, DurationObjectUnits } from 'luxon';
import { check, memoize, type } from '@mews-framework/utils';
import { durationAccessible } from '../componentEnhancers';
import { DurationPart } from '../enums';
import Duration from './duration';

const PARTS = [
    DurationPart.Years,
    DurationPart.Months,
    DurationPart.Weeks,
    DurationPart.Days,
    DurationPart.Hours,
    DurationPart.Minutes,
    DurationPart.Seconds,
] as const;

export const toDurationLuxon = memoize((duration: Duration) => {
    type.check(duration, Duration);
    return DurationLuxon.fromISO(duration.value, { conversionAccuracy: 'longterm' });
});

function toObject(duration: Duration, config?: { partial: true }): DurationObjectUnits;
function toObject(duration: Duration, config: { partial: false }): Required<DurationObjectUnits>;

function toObject(duration: Duration, config: { partial: boolean } = { partial: true }) {
    const durationLuxonObject = toDurationLuxon(duration).toObject();
    if (config?.partial) {
        return durationLuxonObject;
    }
    return {
        years: durationLuxonObject.years ?? 0,
        months: durationLuxonObject.months ?? 0,
        weeks: durationLuxonObject.weeks ?? 0,
        days: durationLuxonObject.days ?? 0,
        hours: durationLuxonObject.hours ?? 0,
        minutes: durationLuxonObject.minutes ?? 0,
        seconds: durationLuxonObject.seconds ?? 0,
    };
}

export default function DurationComponent() {
    return type.create(DurationComponent, DurationService);
}

export const DurationService = {
    ...durationAccessible(toDurationLuxon, PARTS),
    parse: (durationString: string) => {
        check.string(durationString);

        const luxonDuration = DurationLuxon.fromISO(durationString);
        return Duration(luxonDuration);
    },

    negate: (duration: Duration) => Duration(toDurationLuxon(duration).negate()),

    isZero: (duration: Duration) => toDurationLuxon(duration).as('seconds') === 0,

    isPositive: (duration: Duration) => toDurationLuxon(duration).as('seconds') > 0,

    isNegative: (duration: Duration) => toDurationLuxon(duration).as('seconds') < 0,

    asMilliseconds: (duration: Duration) => toDurationLuxon(duration).as('milliseconds'),

    asSeconds: (duration: Duration) => toDurationLuxon(duration).as('seconds'),

    asMinutes: (duration: Duration) => toDurationLuxon(duration).as('minutes'),

    minutes: (duration: Duration) => toDurationLuxon(duration).minutes,

    asHours: (duration: Duration) => toDurationLuxon(duration).as('hours'),

    hours: (duration: Duration) => toDurationLuxon(duration).hours,

    asDays: (duration: Duration) => toDurationLuxon(duration).as('days'),

    asWeeks: (duration: Duration) => toDurationLuxon(duration).as('weeks'),

    asMonths: (duration: Duration) => toDurationLuxon(duration).as('months'),

    as: (unit: keyof DurationObjectUnits) => (duration: Duration) => toDurationLuxon(duration).as(unit),

    fromDays: (days: number) => Duration(DurationLuxon.fromObject({ days })),

    fromObject: (object: DurationLikeObject) => Duration(DurationLuxon.fromObject(object)),

    toObject,

    toString: (duration: Duration) => toDurationLuxon(duration).toString(),

    absolute: (duration: Duration) => {
        const durationLuxon = toDurationLuxon(duration);
        return durationLuxon.as('seconds') < 0 ? Duration(durationLuxon.negate()) : duration;
    },
};
